/**
 * FidoReductionWarningModal — F-012 Phase 3 (IMPL-P3-009).
 *
 * Shown when a fido reduction would push the account's availability below zero
 * (overdraft). The admin must choose how to apply the reduction:
 *   - GRACE  → apply + grant a 7-day grace period (recommended, default)
 *   - BLOCK  → apply + block the wallet immediately
 * or cancel the whole operation.
 *
 * Built on the shadcn Dialog primitive (Radix), which provides the focus trap
 * and `aria-modal` semantics automatically. Initial focus lands on the default
 * "grace" radio option.
 */
import { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

export interface FidoReductionWarningModalProps {
  open: boolean;
  oldFidoEuro: number;
  newFidoEuro: number;
  currentBalanceEuro: number;
  onConfirm: (decision: 'GRACE' | 'BLOCK') => void;
  onCancel: () => void;
}

const EUR = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' });

export function FidoReductionWarningModal({
  open,
  oldFidoEuro,
  newFidoEuro,
  currentBalanceEuro,
  onConfirm,
  onCancel,
}: FidoReductionWarningModalProps) {
  const [decision, setDecision] = useState<'GRACE' | 'BLOCK'>('GRACE');

  // Reset to the recommended default each time the modal is (re)opened.
  useEffect(() => {
    if (open) setDecision('GRACE');
  }, [open]);

  // disponibilità risultante = saldo + nuovo fido (negativa = sconfinamento)
  const availableAfterEuro = currentBalanceEuro + newFidoEuro;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <DialogContent aria-modal="true">
        <DialogHeader>
          <DialogTitle>Riduzione fido con sconfinamento</DialogTitle>
          <DialogDescription>
            La riduzione del fido da {EUR.format(oldFidoEuro)} a {EUR.format(newFidoEuro)} porta la
            disponibilità dell'utente a {EUR.format(availableAfterEuro)}. Scegli come applicare la
            modifica.
          </DialogDescription>
        </DialogHeader>

        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" aria-hidden="true" />
          <AlertTitle>Attenzione: sconfinamento</AlertTitle>
          <AlertDescription>
            Il saldo dell'utente ({EUR.format(currentBalanceEuro)}) non copre il nuovo fido. La
            modifica lascerà l'account in sconfinamento.
          </AlertDescription>
        </Alert>

        <RadioGroup
          value={decision}
          onValueChange={(v) => setDecision(v as 'GRACE' | 'BLOCK')}
          aria-label="Modalità di applicazione della riduzione"
        >
          <div className="flex items-start gap-3 rounded-md border p-3">
            <RadioGroupItem value="GRACE" id="fido-decision-grace" autoFocus />
            <div className="space-y-0.5">
              <Label htmlFor="fido-decision-grace" className="cursor-pointer">
                Grace period 7 giorni (consigliato)
              </Label>
              <p className="text-xs text-muted-foreground">
                L'utente potrà continuare a operare per 7 giorni prima del blocco.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3 rounded-md border p-3">
            <RadioGroupItem value="BLOCK" id="fido-decision-block" />
            <div className="space-y-0.5">
              <Label htmlFor="fido-decision-block" className="cursor-pointer">
                Blocco immediato
              </Label>
              <p className="text-xs text-muted-foreground">
                Il wallet viene bloccato subito: l'utente non potrà registrare nuove pratiche.
              </p>
            </div>
          </div>
        </RadioGroup>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>
            Annulla
          </Button>
          <Button type="button" onClick={() => onConfirm(decision)}>
            Conferma
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
